import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiRequestError, errorMessage } from '../lib/api';
import { humanize } from '../lib/format';
import { SectionLoader } from '../ui';

interface LineTask {
  id: string;
  titolo: string;
  processo: string;
  esiti: string[];
  conferma_obbligatoria: boolean;
  istruzioni: string | null;
  da_comunicare: string | null;
  tentativo: number;
}

/** Chiamate pubbliche della linea operativa: solo il link personale, nessun account. */
async function lineApi<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/v1/line/${path}`, body === undefined ? {} : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({ code: 'invalid_response', message: 'Risposta non valida' }));
  if (!res.ok) throw new ApiRequestError(res.status, data);
  return data as T;
}

function TaskItem({ token, task }: { token: string; task: LineTask }) {
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState(false);
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['line', token] });
  const close = useMutation({ mutationFn: (outcome?: string) => lineApi<void>(`${token}/tasks/${task.id}/close`, { ...(outcome ? { outcome } : {}), ...(task.conferma_obbligatoria ? { confirmed } : {}) }), onSuccess: refresh });
  const report = useMutation({
    mutationFn: () => lineApi<void>(`${token}/tasks/${task.id}/report`, { note }),
    onSuccess: () => {
      setProblem(false);
      setNote('');
    },
  });
  const blocked = close.isPending || (task.conferma_obbligatoria && !confirmed);
  return (
    <li className="rounded-2xl bg-white p-4 shadow-sm">
      <p className="text-lg font-semibold leading-snug">{task.titolo}</p>
      <p className="text-sm text-ink/50">
        {task.processo}
        {task.tentativo > 1 && ' · da rifare'}
      </p>
      {task.istruzioni && <p className="mt-2 text-sm text-ink/75">{task.istruzioni}</p>}
      {task.da_comunicare && <p className="mt-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900">Da comunicare: {task.da_comunicare}</p>}
      {task.conferma_obbligatoria && (
        <label className="mt-3 flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1 h-5 w-5" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} /> Confermo di averlo fatto davvero
        </label>
      )}
      <div className="mt-4 grid grid-cols-2 gap-3">
        {task.esiti.length ? (
          task.esiti.map((o) => (
            <button key={o} disabled={blocked} onClick={() => close.mutate(o)} className={`rounded-xl py-4 text-lg font-semibold text-white disabled:opacity-40 ${o === 'fallito' ? 'bg-red-600' : 'bg-emerald-600'}`}>
              {humanize(o)}
            </button>
          ))
        ) : (
          <button disabled={blocked} onClick={() => close.mutate(undefined)} className="rounded-xl bg-emerald-600 py-4 text-lg font-semibold text-white disabled:opacity-40">
            Fatto
          </button>
        )}
        <button onClick={() => setProblem(!problem)} className="rounded-xl bg-amber-500 py-4 text-lg font-semibold text-white">
          Problema
        </button>
      </div>
      {problem && (
        <div className="mt-3 space-y-2">
          <textarea className="h-24 w-full rounded-xl border border-black/15 p-3 text-base" placeholder="Cosa non va?" value={note} onChange={(e) => setNote(e.target.value)} />
          <button disabled={!note.trim() || report.isPending} onClick={() => report.mutate()} className="w-full rounded-xl bg-ink py-3 font-semibold text-white disabled:opacity-40">
            Invia segnalazione
          </button>
        </div>
      )}
      {report.isSuccess && <p className="mt-2 text-sm text-emerald-700">Segnalazione inviata al responsabile.</p>}
      {(close.error || report.error) && <p className="mt-2 text-sm text-red-700">{errorMessage(close.error ?? report.error)}</p>}
    </li>
  );
}

/** Pagina della linea operativa: i miei compiti, due bottoni, nient'altro. Si aggiorna da sola. */
export function LinePage({ token }: { token: string }) {
  const page = useQuery({
    queryKey: ['line', token],
    queryFn: () => lineApi<{ azienda: string; persona: string; compiti: LineTask[] }>(token),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  if (page.error) {
    return <main className="flex min-h-screen items-center justify-center px-6 text-center text-ink/70">Questo link non è più valido. Chiedi al tuo responsabile un link nuovo.</main>;
  }
  return (
    <main className="mx-auto min-h-screen max-w-lg px-4 py-6">
      {page.data && (
        <>
          <p className="text-sm text-ink/50">{page.data.azienda}</p>
          <h1 className="mb-5 text-2xl font-semibold">Ciao {page.data.persona}</h1>
          {page.data.compiti.length === 0 ? (
            <p className="rounded-2xl bg-white p-6 text-center text-lg text-ink/60 shadow-sm">Nessun compito per ora. 👍</p>
          ) : (
            <ul className="space-y-4">
              {page.data.compiti.map((task) => (
                <TaskItem key={task.id} token={token} task={task} />
              ))}
            </ul>
          )}
        </>
      )}
      {page.isLoading && <SectionLoader />}
    </main>
  );
}
